import * as THREE from 'three';
import { createTextSprite } from '../../../utils/createTextSprite';
import { drawTextFromCode } from '../../../commands/drawTextFromCode';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract geometry position data as a flat Float32-compatible array.
 * Returns null when the object has no geometry or no position attribute.
 */
function extractGeometry(obj) {
  if (!obj.geometry) return null;
  const posAttr = obj.geometry.getAttribute?.('position');
  if (!posAttr) return null;
  const position = Array.from(posAttr.array);
  // INDEXED geometriler (CircleGeometry/PlaneGeometry — buat, dolu daire/kare
  // solid'ler) index ile üçgen fan tanımlar. Index serialize EDİLMEZSE restore'da
  // vertex'ler non-indexed üçgen listesi sanılıp daire DİLİM/wedge olarak çiziliyor
  // (buat'lar kayıttan açılınca dilim oluyordu). Index varsa onu da sakla.
  const indexAttr = obj.geometry.getIndex?.();
  // UV: canvas-texture'lı text mesh'leri (drawTextFromCode — armatür/makine etiketleri)
  // PlaneGeometry + map kullanır. UV serialize EDİLMEZSE restore'da map UV bulamaz →
  // texture render edilmez (text görünmez/beyaz). Map deserialize'da userData.text'ten
  // regenere edilir; ama UV olmadan işe yaramaz, bu yüzden UV'yi de sakla.
  const uvAttr = obj.geometry.getAttribute?.('uv');
  if (indexAttr || uvAttr) {
    const out = { position };
    if (indexAttr) out.index = Array.from(indexAttr.array);
    if (uvAttr) out.uv = Array.from(uvAttr.array);
    return out;
  }
  // Index/UV yok (Line/non-indexed wire, floor plan): düz dizi (geriye dönük uyumlu).
  return position;
}

/**
 * Extract material properties we care about.
 * Works for a single material or an array (picks the first).
 */
function extractMaterial(obj) {
  const mat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
  if (!mat) return null;
  return {
    type: mat.type || 'LineBasicMaterial',
    color: mat.color ? mat.color.getHex() : 0xffffff,
    opacity: mat.opacity ?? 1,
    transparent: mat.transparent ?? false,
    linewidth: mat.linewidth ?? undefined,
    depthWrite: mat.depthWrite ?? true,
    side: mat.side ?? THREE.FrontSide,
  };
}

/**
 * Returns true for objects that should be skipped during serialization.
 * @param {THREE.Object3D} obj
 * @param {string} tabId - which tab this object belongs to
 * @param {boolean} includeAll - when true, only skip lights/cameras/helpers (for viewer export)
 */
function shouldSkip(obj, tabId, includeAll = false) {
  // Skip lights, cameras, helpers — always
  if (obj.isLight || obj.isCamera) return true;
  if (obj.type === 'AmbientLight' || obj.type === 'DirectionalLight' ||
      obj.type === 'PointLight' || obj.type === 'SpotLight' ||
      obj.type === 'HemisphereLight') return true;
  if (obj.type === 'PerspectiveCamera' || obj.type === 'OrthographicCamera') return true;
  if (obj.isHelper || /Helper$/.test(obj.type)) return true;

  // Gizmo'lar runtime-only UI yardımcıları (hat/mtext/arc/ellipse düzenleme tutamakları;
  // name '__gizmo*' + userData.isGizmo). Bir obje seçiliyken kayıt yapılırsa gizmo'lar
  // snapshot'a giriyor, reload'da kalıcı görünüp İPTAL EDİLEMİYORDU. Her zaman atla.
  if (obj.userData?.isGizmo === true) return true;
  if (typeof obj.name === 'string' && obj.name.startsWith('__gizmo')) return true;

  // Explicit opt-out — runtime-only container mesh'leri (örn. fpMesh) buraya girer.
  // [SAVE-LOAD-REWRITE Aşama A] fpMesh ÖZEL: skipSerialize taşısa bile tam
  // serialize edilir (collectSerializedChildren'da hoist yerine doğrudan
  // serializeObject çağrısı). Sebep: Y kayması (sweep WORLD→LOCAL hesabı).
  if (obj.userData?.skipSerialize && obj.userData?.isFloorPlanMesh !== true) return true;

  // In includeAll mode (viewer export), keep DXF & user-drawn objects
  if (!includeAll) {
    // Skip DXF-sourced and user-drawn objects on ALL tabs (they're in Redux dxf slice,
    // dxfDrawCore rebuilds them on load — saving them here causes duplicates)
    if (obj.userData?.source === 'dxf') return true;
    if (obj.userData?._userDrawn === true) return true;
  }

  // For NON-MIMARI tabs: save EVERYTHING (layout frames, documents, symbols, etc.)
  // Only skip truly empty objects with no geometry AND no children
  const hasGeometry = !!obj.geometry;
  const isGroup = obj.type === 'Group';
  const isSprite = obj.type === 'Sprite';
  const hasChildren = obj.children && obj.children.length > 0;

  if (!hasGeometry && !isGroup && !isSprite && !hasChildren) return true;

  return false;
}

// ---------------------------------------------------------------------------
// Serialize a single Three.js object (recursive for Groups)
// ---------------------------------------------------------------------------

function serializeObject(obj, tabId, includeAll = false) {
  if (!obj || shouldSkip(obj, tabId, includeAll)) return null;

  const entry = {
    type: obj.type || 'Object3D',
    // uuid KRİTİK: snap sistemi (getSnapPointsFromScene / snapWallHandler) wire
    // mesh'ini Redux wire'ın sceneUUID'si ile getObjectByProperty('uuid', ...) bulur.
    // uuid round-trip edilmezse deserialize rastgele uuid atar → snapshot'tan gelen
    // wire'da snap/klavuz çizgileri oluşmaz (özellikle isSelectable taşımayan socket
    // wire'larında, çünkü scene-traverse fallback'i de onları atlar).
    uuid: obj.uuid,
    // name KRİTİK: panel/kat planı kodu child'ları isimle bulur
    // (örn. 'offsetGroup', 'panel-outer', 'panel-hatch', 'outSnapGroup').
    // İsim kaydedilmezse restore sonrası bu lookup'lar undefined döner →
    // panel taraması görünmez + panel düzenleme (panel ekle) çöker.
    ...(obj.name ? { name: obj.name } : {}),
    geometryData: extractGeometry(obj),
    materialData: extractMaterial(obj),
    userData: { ...obj.userData },
    position: {
      x: obj.position?.x ?? 0,
      y: obj.position?.y ?? 0,
      z: obj.position?.z ?? 0,
    },
    rotation: {
      x: obj.rotation?.x ?? 0,
      y: obj.rotation?.y ?? 0,
      z: obj.rotation?.z ?? 0,
    },
    scale: {
      x: obj.scale?.x ?? 1,
      y: obj.scale?.y ?? 1,
      z: obj.scale?.z ?? 1,
    },
    visible: obj.visible ?? true,
  };

  // Recursively serialize children for Groups and any object with children
  if (obj.children?.length) {
    const serializedChildren = collectSerializedChildren(obj.children, tabId, includeAll);
    if (serializedChildren.length > 0) {
      entry.children = serializedChildren;
    }
  }

  return entry;
}

/**
 * Çocukları serialize ederken skipSerialize konteyner'lerini "şeffaf" yap:
 * konteyner'in kendisi atlanır ama çocukları üst seviyeye taşınır, konteyner'in
 * position offset'i bu çocuklara eklenir (world transform korunur).
 *
 * Tipik kullanım: fpMesh (mesh-FP container) skipSerialize. fpMesh'in altındaki
 * tesisat (machine, cableShaft) ve floorPlanGroup üst seviyeye hoist edilir.
 * Reload'da bu öğeler ön ataçlı oldukları parent'a (documentGroup) eklenir;
 * useTesisatMeshSync sweep sonra tesisat'ı tekrar fpMesh'e attach eder.
 *
 * Varsayım: skipSerialize konteyner'ları sadece translate (rotation/scale yok).
 * fpMesh bu varsayımı karşılar (createFloorPlanMesh sade plane mesh).
 */
function collectSerializedChildren(children, tabId, includeAll) {
  const out = [];
  for (const child of children) {
    if (child?.userData?.skipSerialize) {
      // [SAVE-LOAD-REWRITE Aşama A] fpMesh (mesh-FP container) artık TAM
      // serialize edilir — children hoist edilmez. Sebep: hoist sonrası
      // tesisat scene root'a düşüyordu; restore'da sweep onu fpMesh'e
      // tekrar attach ederken WORLD→LOCAL hesabı Y koord'unu kaydırıyordu.
      // fpMesh tam serialize → restore'da tesisat zaten fpMesh-child →
      // sweep no-op → Y kayması yok.
      if (child?.userData?.isFloorPlanMesh === true) {
        const sc = serializeObject(child, tabId, includeAll);
        if (sc) out.push(sc);
        continue;
      }
      // Diğer skipSerialize container'lar için eski hoist davranışı korunur.
      if (child.children?.length) {
        const hoisted = collectSerializedChildren(child.children, tabId, includeAll);
        for (const h of hoisted) {
          h.position.x += child.position?.x ?? 0;
          h.position.y += child.position?.y ?? 0;
          h.position.z += child.position?.z ?? 0;
          out.push(h);
        }
      }
    } else {
      const sc = serializeObject(child, tabId, includeAll);
      if (sc) out.push(sc);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Traverse every tab scene in `scenesRef` and return serializable data.
 *
 * @param {{ current: Record<string, THREE.Scene> }} scenesRef
 * @param {{ includeAll?: boolean }} options - includeAll: include DXF/user objects (for viewer export)
 * @returns {Record<string, object[]>}  tabId -> array of serialized objects
 */
export function serializeScenes(scenesRef, { includeAll = false } = {}) {
  const result = {};
  if (!scenesRef?.current) return result;

  for (const [tabId, scene] of Object.entries(scenesRef.current)) {
    if (!scene || !scene.children) continue;
    // Top-level: skipSerialize konteyner'ı doğrudan scene altındaysa
    // (örn. ileride fpMesh direkt scene-child olursa) çocuklarını hoist et.
    const objects = collectSerializedChildren(scene.children, tabId, includeAll);

    if (objects.length > 0) {
      result[tabId] = objects;
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Deserialization helpers
// ---------------------------------------------------------------------------

/**
 * Create a Three.js material from serialized data.
 */
function createMaterial(matData, objectType) {
  if (!matData) {
    // Sensible default depending on object type
    if (objectType === 'Mesh') return new THREE.MeshBasicMaterial({ color: 0xffffff });
    if (objectType === 'Sprite') return new THREE.SpriteMaterial({ color: 0xffffff });
    return new THREE.LineBasicMaterial({ color: 0xffffff });
  }

  const color = matData.color ?? 0xffffff;
  const opacity = matData.opacity ?? 1;
  const transparent = matData.transparent ?? false;
  const depthWrite = matData.depthWrite ?? true;
  const side = matData.side ?? THREE.FrontSide;

  switch (matData.type) {
    case 'MeshBasicMaterial':
      return new THREE.MeshBasicMaterial({ color, opacity, transparent, depthWrite, side });
    case 'MeshStandardMaterial':
      return new THREE.MeshStandardMaterial({ color, opacity, transparent, depthWrite, side });
    case 'MeshLambertMaterial':
      return new THREE.MeshLambertMaterial({ color, opacity, transparent, depthWrite, side });
    case 'SpriteMaterial':
      return new THREE.SpriteMaterial({ color, opacity, transparent, depthWrite });
    case 'LineDashedMaterial':
      return new THREE.LineDashedMaterial({
        color, opacity, transparent, depthWrite,
        linewidth: matData.linewidth ?? 1,
      });
    case 'LineBasicMaterial':
    default:
      return new THREE.LineBasicMaterial({
        color, opacity, transparent, depthWrite,
        linewidth: matData.linewidth ?? 1,
      });
  }
}

/**
 * Create a BufferGeometry from a flat positions array.
 *
 * NaN/outlier-defense: position array'inde NaN vertex varsa veya vertex'ler
 * sahnenin tipik boyutunu çok aşıyorsa (tek doküman max ~2300u, 8000+ outlier
 * sayılır), geometry oluşturma — sahneye eklenmesin. Sebep: DXF parse bazı
 * polyline'ları bozuk üretip JBZ'ye save ediyor. Save edilen NaN vertex'ler
 * reload'da THREE-NaN-patch tarafından (0,0)'a çekiliyor → polyline (X,Y)
 * ile (0,0) arasında uzanan stretch duvar görünüyor. Burada erken müdahale:
 * bozuk geometry hiç sahneye girmez.
 */
const SCENE_OUTLIER_THRESHOLD = 8000; // tek doküman max ~2300, 8000+ kesin outlier
function createGeometry(geomData) {
  if (!geomData) return null;
  // Yeni format: {position, index, uv}; eski/Line format: düz dizi.
  const positions = Array.isArray(geomData) ? geomData : geomData.position;
  const index = Array.isArray(geomData) ? null : geomData.index;
  const uv = Array.isArray(geomData) ? null : geomData.uv;
  if (!positions || positions.length === 0) return null;

  // Vertex tarama: NaN + outlier (vertex spread)
  let hasNaN = false;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i], y = positions[i + 1];
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      hasNaN = true;
      break;
    }
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (hasNaN) return null;
  const spreadX = maxX - minX;
  const spreadY = maxY - minY;
  if (spreadX > SCENE_OUTLIER_THRESHOLD || spreadY > SCENE_OUTLIER_THRESHOLD) {
    return null;
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  // INDEXED solid'ler (CircleGeometry/PlaneGeometry — buat vb.): index'i geri yükle
  // ki üçgen fan doğru kurulsun (yoksa daire dilim/wedge çiziliyor).
  if (index && index.length > 0) {
    geom.setIndex(index);
  }
  // UV: canvas-texture'lı text mesh'lerinde (drawTextFromCode) map'in render edilebilmesi
  // için şart (map deserialize'da regenere edilir ama UV olmadan görünmez).
  if (uv && uv.length > 0) {
    geom.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  }
  return geom;
}

/**
 * Recreate a single Three.js object from serialized data (recursive for groups).
 *
 * [SAVE-LOAD-REWRITE] export: JbzViewer (proje ön izleme) bu fonksiyonu birebir
 * kullanır. Eskiden JbzViewer kendi kopya deserializer'ını tutuyordu; format
 * değişince (mesh-fp: {position,index,uv} geometri, text regen) sapıp ön izlemeyi
 * bozuyordu. Tek kaynak = editör ile ön izleme senkron kalır.
 */
export function deserializeObject(data) {
  if (!data) return null;

  // Gizmo'lar runtime-only — yeni kayıtlarda zaten atlanıyor (shouldSkip), ama ESKİ
  // kayıtlarda snapshot'a girmiş olabilir. Restore ETME; yoksa reload'da kalıcı görünüp
  // iptal edilemez (kullanıcı raporu).
  if (data.userData?.isGizmo === true) return null;
  if (typeof data.name === 'string' && data.name.startsWith('__gizmo')) return null;

  let obj = null;

  try {
    const type = data.type || 'Line';

    switch (type) {
      case 'Group':
      case 'Object3D': {
        obj = new THREE.Group();
        break;
      }

      case 'Sprite': {
        const mat = createMaterial(data.materialData, 'Sprite');
        obj = new THREE.Sprite(mat);
        break;
      }

      case 'Mesh': {
        const geom = createGeometry(data.geometryData);
        if (!geom) return null;
        const mat = createMaterial(data.materialData, 'Mesh');
        obj = new THREE.Mesh(geom, mat);
        break;
      }

      case 'LineSegments': {
        const geom = createGeometry(data.geometryData);
        if (!geom) return null;
        const mat = createMaterial(data.materialData, 'LineSegments');
        obj = new THREE.LineSegments(geom, mat);
        break;
      }

      case 'LineLoop': {
        const geom = createGeometry(data.geometryData);
        if (!geom) return null;
        const mat = createMaterial(data.materialData, 'LineLoop');
        obj = new THREE.LineLoop(geom, mat);
        break;
      }

      case 'Line':
      default: {
        const geom = createGeometry(data.geometryData);
        if (!geom) return null;
        const mat = createMaterial(data.materialData, 'Line');
        obj = new THREE.Line(geom, mat);
        break;
      }
    }
  } catch (err) {
    console.warn('[sceneSerializer] Failed to deserialize object:', err, data);
    return null;
  }

  if (!obj) return null;

  // uuid'yi geri yükle: snap sistemi wire mesh'ini Redux sceneUUID ile bu uuid
  // üzerinden bulur (yoksa rastgele uuid → snap/klavuz çizgileri oluşmaz).
  if (data.uuid) obj.uuid = data.uuid;

  // Restore transform
  if (data.position) {
    obj.position.set(
      data.position.x ?? 0,
      data.position.y ?? 0,
      data.position.z ?? 0,
    );
  }
  if (data.rotation) {
    obj.rotation.set(
      data.rotation.x ?? 0,
      data.rotation.y ?? 0,
      data.rotation.z ?? 0,
    );
  }
  if (data.scale) {
    obj.scale.set(
      data.scale.x ?? 1,
      data.scale.y ?? 1,
      data.scale.z ?? 1,
    );
  }

  obj.visible = data.visible ?? true;

  // Restore name (panel/kat planı child lookup'ları buna bağlı — bkz. serializeObject)
  if (data.name) obj.name = data.name;

  // Click-overlay hatch'leri (panel/box tıklama alanı) yalnızca raycast içindir,
  // ASLA boyanmamalı. Kayda transient hover/seçim state'i sızıp opacity'yi 0.01'den
  // 1'e çekebiliyor → opak sarı/cyan üçgen paneli kaplıyordu. colorWrite=false ile
  // renk/opacity ne olursa olsun görünmez kalır; raycast (tıklanabilirlik) etkilenmez.
  const _nm = obj.name || '';
  if ((_nm === 'box-hatch' || _nm.endsWith('click-hatch')) && obj.material) {
    const _mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    _mats.forEach(m => { if (m) { m.colorWrite = false; m.needsUpdate = true; } });
  }

  // Restore userData
  if (data.userData && typeof data.userData === 'object') {
    obj.userData = { ...data.userData };
  }

  // Text Sprite (panel başlığı, etiket vb): canvas texture (map) serialize
  // EDİLMEZ → restore'da map'siz beyaz dörtgen görünüyordu. Metin userData.text'te
  // korunuyor; texture'ı createTextSprite ile yeniden üret. (Arial canvas; DXF
  // mtext'lerin TIMES.TTF font 404'üyle ilgisiz.)
  if (obj.isSprite && data.userData?.text && typeof document !== 'undefined') {
    try {
      const fs = data.userData.fontSize ? data.userData.fontSize * 10 : 48;
      const regen = createTextSprite(String(data.userData.text), { fontSize: fs });
      if (regen?.material) {
        if (data.materialData?.color != null) regen.material.color.setHex(data.materialData.color);
        obj.material = regen.material; // map (text canvas) içerir
      }
    } catch (_) { /* DOM yoksa veya hata olursa beyaz sprite kalır — kritik değil */ }
  }

  // Restore children (for Groups and any object with serialized children)
  if (Array.isArray(data.children)) {
    for (const childData of data.children) {
      const child = deserializeObject(childData);
      if (child) obj.add(child);
    }
  }

  // drawTextFromCode tabanlı text (armatür/makine/anahtar etiketleri): Group(type='text')
  // + PlaneGeometry mesh + MeshBasicMaterial(map=canvas). Material map serialize EDİLMEZ →
  // restore'da BEYAZ DÖRTGEN (panel başlığı Sprite yolundan çözülmüştü; bu Mesh yolu).
  // userData.text'ten canvas texture'ı yeniden üretip mesh material'larına uygula.
  // (UV artık serialize ediliyor → map render olur; children restore EDİLDİKTEN sonra
  // çalışmalı ki text mesh'i obj.traverse'de bulunsun. Sprite'lar zaten yukarıda ele alındı.)
  if (!obj.isSprite && data.userData?.type === 'text' && data.userData?.text && typeof document !== 'undefined') {
    try {
      const fresh = drawTextFromCode(new THREE.Scene(), {
        text: String(data.userData.text),
        position: { x: 0, y: 0 },
        fontSize: data.userData.fontSize,
        color: data.userData.color || '#ffffff',
        alignment: data.userData.alignment,
        halign: data.userData.halign,
        valign: data.userData.valign,
        rotation: 0,
      });
      let map = null;
      fresh?.traverse?.((c) => { if (!map && c.isMesh && c.material && c.material.map) map = c.material.map; });
      if (map) {
        obj.traverse((c) => {
          // UV GUARD: map ancak UV'si olan mesh'te render olur. Eski kayıtlarda UV
          // serialize edilmemiş; map'i UV'siz uygulamak text'i GÖRÜNMEZ yapar (beyaz'dan
          // beter). UV varsa uygula (yeni kayıtlar) → düzelir; yoksa dokunma (eski kayıt
          // beyaz kalır, re-save ile düzelir).
          if (c.isMesh && c.material && !Array.isArray(c.material) && c.geometry?.getAttribute?.('uv')) {
            c.material.map = map;
            c.material.transparent = true;
            c.material.needsUpdate = true;
          }
        });
      }
    } catch (_) { /* DOM/canvas yoksa beyaz kalır — kritik değil */ }
  }

  return obj;
}

// ---------------------------------------------------------------------------

/**
 * Recreate Three.js scenes from serialized data and populate scenesRef.
 *
 * @param {{ current: Record<string, THREE.Scene> }} scenesRef
 * @param {Record<string, object[]>} data  - output of serializeScenes()
 */
// [SAVE-LOAD-REWRITE] Tab key alias mapping — scenesRef.current key'i
// kullanıcının setup'ına göre legacy ID (elektrik) VEYA backend code
// (strong_current) olabilir. snapshot'tan gelen tabId hangisi olursa
// olsun, scenesRef'te ZATEN VAR olan adayı tercih ederiz → snapshot
// doğru scene'e yüklenir, useElectricalLayout aynı scene'i okur.
const TAB_KEY_ALIASES = {
  mimari:             ['mimari', 'architectural'],
  architectural:      ['mimari', 'architectural'],
  elektrik:           ['strong_current', 'elektrik'],
  strong_current:     ['strong_current', 'elektrik'],
  telekom:            ['weak_current', 'telekom'],
  weak_current:       ['weak_current', 'telekom'],
  yangin:             ['fire_detection', 'yangin'],
  fire_detection:     ['fire_detection', 'yangin'],
  santiye:            ['construction_site', 'santiye'],
  construction_site:  ['construction_site', 'santiye'],
  baglanti:           ['connection_line', 'baglanti'],
  connection_line:    ['connection_line', 'baglanti'],
};

function resolveSceneKey(scenesRef, tabId) {
  const candidates = TAB_KEY_ALIASES[tabId] || [tabId];
  for (const c of candidates) {
    if (scenesRef?.current?.[c]) return c;
  }
  return candidates[0];
}

export function deserializeScenes(scenesRef, data) {
  // eslint-disable-next-line no-console
  console.log('[LOAD:deserializeScenes-start]', JSON.stringify({
    dataKeys: data ? Object.keys(data) : null,
    scenesRefKeys: Object.keys(scenesRef?.current || {}),
  }));
  if (!scenesRef || !data || typeof data !== 'object') {
    // eslint-disable-next-line no-console
    console.log('[LOAD:deserializeScenes-EARLY-RETURN]', { scenesRef: !!scenesRef, data: !!data });
    return;
  }
  if (!scenesRef.current) scenesRef.current = {};

  for (const [tabId, objects] of Object.entries(data)) {
    if (!Array.isArray(objects)) continue;

    // [SAVE-LOAD-REWRITE] Alias resolution: snapshot tabId 'elektrik' olsa da
    // scenesRef'te 'strong_current' varsa onu kullan (kullanıcının aktif scene'i).
    const sceneKey = resolveSceneKey(scenesRef, tabId);
    const sceneExisted = !!scenesRef.current[sceneKey];
    if (!scenesRef.current[sceneKey]) {
      scenesRef.current[sceneKey] = new THREE.Scene();
    }

    const scene = scenesRef.current[sceneKey];
    let added = 0;
    let failed = 0;

    for (const objData of objects) {
      try {
        const obj = deserializeObject(objData);
        if (obj) {
          scene.add(obj);
          added++;
        }
      } catch (err) {
        failed++;
        // eslint-disable-next-line no-console
        console.log(`[LOAD:deserialize-obj-FAILED] tab=${tabId}`, err);
      }
    }
    // eslint-disable-next-line no-console
    console.log('[LOAD:deserializeScenes-tab-done]', JSON.stringify({
      tabId, sceneKey, sceneExisted, added, failed, totalNow: scene.children.length,
    }));
  }
  // eslint-disable-next-line no-console
  console.log('[LOAD:deserializeScenes-end]', JSON.stringify({
    scenesRefKeys: Object.keys(scenesRef.current || {}),
    childCounts: Object.fromEntries(
      Object.entries(scenesRef.current || {}).map(([k, s]) => [k, s?.children?.length ?? '?'])
    ),
  }));
}
